/**
 * Structured KOT slip — the one kitchen-ticket layout for every print path.
 *
 * A printer KOT travels as two things: `payload`, the 32-column monospace
 * text the Android ESC/POS agent feeds straight to the printer, and `slip`,
 * the typed structure below, which the desktop/browser path lays out as HTML
 * (rules as borders, items as a grid) so nothing wraps or bleeds whatever the
 * configured font scale or paper width.
 *
 * `renderKotSlipText` is a line-for-line mirror of the backend's
 * renderKOTSlipText (backend/internal/handlers/kot_slip.go). The two are
 * pinned byte-for-byte equal by the fixtures under
 * backend/internal/handlers/testdata/kotslip/ — kotSlip.test.ts reads the same
 * files. Change one renderer, change both, update the goldens.
 */

export type KotSlipKind = 'fire' | 'void' | 'reprint' | 'test' | 'offline' | 'online'

export interface KotSlipMeta {
  left: string
  right: string
}

export interface KotSlipItem {
  name: string
  qty: number
  /**
   * Chosen modifier options, pre-formatted by the caller ("Extra Shot",
   * "Extra Shot x2"). The renderer stays dumb about quantity formatting so the
   * byte-for-byte contract with kot_slip.go stays narrow.
   */
  mods?: string[]
  note?: string
}

export interface KotSlip {
  kind: KotSlipKind | string
  /** KITCHEN ORDER | *** VOID KOT *** | *** TEST KOT *** */
  title: string
  /** Centred lines under the title, in order (">>> DINE IN <<<", "*** REPRINT 14:50 ***"). */
  banners: string[]
  /** "" => no TABLE banner. */
  table: string
  /** left/right rows; right === "" => left is wrapped at full width. */
  meta: KotSlipMeta[]
  /** Order-note lines; empty => block omitted. */
  note: string[]
  items: KotSlipItem[]
  /** Centred lines boxed between two thick rules at the bottom. */
  footer: string[]
}

/** Printer font A is 32 cols on 58 mm and 48 on 80 mm, so 32 fits both. */
export const KOT_WIDTH = 32
/**
 * Item-name column; the quantity is centred in the remaining columns, so a
 * single digit lands ~80 % across the slip. The HTML grid in kotSlipCss()
 * (`minmax(0, 1fr) 36%`, qty centred) puts it in the same place. Move both —
 * and the Go constants — together to shift the QTY column.
 */
export const KOT_NAME_COL_W = 18
export const KOT_QTY_COL_W = KOT_WIDTH - KOT_NAME_COL_W
const NOTE_INDENT = '  >> '
/**
 * Prefix of a chosen-modifier line (F-MOD-N6). Deliberately distinct from
 * NOTE_INDENT: '>>' is the mark the kitchen reads as "an exception, look
 * carefully", while a modifier is the normal case. Mirrors kotModIndent.
 */
const MOD_INDENT = '  - '

/**
 * Column widths for a slip of `width` characters (F-FE-N111).
 *
 * The name/qty split stays PROPORTIONAL rather than fixed, which is what holds
 * the quantity in the same place on the paper at any width: at 32 the digit
 * lands 78% across, and at 46 it still does. A fixed 18-column name would have
 * drifted it toward the middle of a wider slip.
 */
export function kotColumns(width: number): { nameW: number; qtyW: number } {
  const qtyW = Math.max(3, Math.round((width * KOT_QTY_COL_W) / KOT_WIDTH))
  return { nameW: Math.max(4, width - qtyW), qtyW }
}

export function kotSlipTitleFor(kind: string): string {
  if (kind === 'void') return '*** VOID KOT ***'
  if (kind === 'test') return '*** TEST KOT ***'
  return 'KITCHEN ORDER'
}

/** "" for a table value not worth printing — mirrors kotSlipTable in Go. */
export function kotSlipTable(tableNo: string): string {
  const tn = (tableNo ?? '').trim()
  if (tn === '' || tn.toUpperCase() === 'N/A' || tn === '—' || tn === '-') return ''
  return tn
}

/** Fresh slip with every collection initialised. */
export function newKotSlip(kind: KotSlipKind | string, title = kotSlipTitleFor(kind)): KotSlip {
  return { kind, title, banners: [], table: '', meta: [], note: [], items: [], footer: [] }
}

// ── text renderer (mirror of kot_slip.go) ───────────────────────────────────

const runeLen = (s: string): number => Array.from(s).length

function centerIn(s: string, width: number): string {
  const n = runeLen(s)
  if (n >= width) return s
  return ' '.repeat(Math.floor((width - n) / 2)) + s
}

function padRight(s: string, width: number): string {
  const n = runeLen(s)
  if (n >= width) return s
  return s + ' '.repeat(width - n)
}

export function centerKotLine(s: string, width = KOT_WIDTH): string {
  return centerIn(s.trim(), width)
}

/**
 * `left` flush-left and `right` flush-right on one line. When both don't fit,
 * `left` is truncated (to at least 8 runes) so the right-hand value —
 * station name, fired time — survives.
 */
export function kotLeftRight(leftIn: string, rightIn: string, width = KOT_WIDTH): string {
  let left = leftIn.trim()
  const right = rightIn.trim()
  let ln = runeLen(left)
  const rn = runeLen(right)
  if (ln + 2 + rn > width) {
    const maxLeft = Math.max(8, width - 2 - rn)
    if (ln > maxLeft) {
      left = Array.from(left).slice(0, maxLeft).join('')
      ln = maxLeft
    }
  }
  const gap = Math.max(1, width - ln - rn)
  return left + ' '.repeat(gap) + right
}

/**
 * Word-wrap with the first line `firstW` wide and following lines `restW`
 * wide; a single word wider than the line is hard-split. Empty input yields
 * no lines.
 */
export function kotWrapFirstRest(input: string, firstW: number, restW: number): string[] {
  const s = input.trim()
  if (s === '') return []
  const words = s.split(/\s+/)
  const out: string[] = []
  let cur = ''
  let curN = 0
  let limit = firstW
  const flush = () => {
    out.push(cur)
    cur = ''
    curN = 0
    limit = restW
  }
  for (const w of words) {
    let wr = Array.from(w)
    while (wr.length > 0) {
      const wn = wr.length
      if (curN === 0) {
        if (wn <= limit) {
          cur = wr.join('')
          curN = wn
          wr = []
          continue
        }
        cur = wr.slice(0, limit).join('')
        curN = limit
        wr = wr.slice(limit)
        flush()
        continue
      }
      if (curN + 1 + wn <= limit) {
        cur += ' ' + wr.join('')
        curN += 1 + wn
        wr = []
        continue
      }
      flush()
    }
  }
  if (curN > 0) out.push(cur)
  return out
}

export function kotWrap(s: string, width: number): string[] {
  return kotWrapFirstRest(s, width, width)
}

/** The 32-column plain-text ticket — byte-identical to the Go renderer. */
export function renderKotSlipText(s: KotSlip, width = KOT_WIDTH): string {
  const rule = '='.repeat(width)
  const thin = '-'.repeat(width)
  const { nameW, qtyW } = kotColumns(width)
  const lines: string[] = []
  lines.push(rule, centerKotLine(s.title, width))
  for (const b of s.banners) lines.push(centerKotLine(b, width))
  const tn = (s.table ?? '').trim()
  if (tn) lines.push(rule, centerKotLine(`TABLE ${tn.toUpperCase()}`, width))
  lines.push(rule)

  for (const m of s.meta) {
    if ((m.right ?? '').trim() === '') {
      lines.push(...kotWrap(m.left ?? '', width))
      continue
    }
    lines.push(kotLeftRight(m.left ?? '', m.right, width))
  }

  if (s.note.length > 0) {
    lines.push(thin, '** ORDER NOTE **')
    for (const n of s.note) lines.push(...kotWrap(n, width))
  }

  lines.push(thin, padRight('ITEM', nameW) + centerIn('QTY', qtyW), thin)
  for (const it of s.items) {
    let name = (it.name ?? '').trim()
    if (s.kind === 'void') name = 'X ' + name
    let chunks = kotWrapFirstRest(name, nameW, nameW - 2)
    if (chunks.length === 0) chunks = ['']
    lines.push(padRight(chunks[0], nameW) + centerIn(String(it.qty), qtyW))
    for (const c of chunks.slice(1)) lines.push('  ' + c)
    // Modifiers before the note: they complete the description of WHAT was
    // ordered, while the note annotates it.
    for (const raw of it.mods ?? []) {
      const m = raw.trim()
      if (!m) continue
      const ml = kotWrap(m, width - MOD_INDENT.length)
      ml.forEach((c, i) => lines.push((i === 0 ? MOD_INDENT : ' '.repeat(MOD_INDENT.length)) + c))
    }
    const note = (it.note ?? '').trim()
    if (note) {
      const nl = kotWrap(note, width - NOTE_INDENT.length)
      nl.forEach((c, i) => lines.push((i === 0 ? NOTE_INDENT : ' '.repeat(NOTE_INDENT.length)) + c))
    }
  }
  lines.push(rule)

  if (s.footer.length > 0) {
    for (const f of s.footer) lines.push(centerKotLine(f, width))
    lines.push(rule)
  }

  return lines.map((ln) => ln.replace(/ +$/, '')).join('\n') + '\n'
}

// ── HTML renderer (desktop / browser print path) ────────────────────────────

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

const el = (cls: string, inner: string) => `<div class="${cls}">${inner}</div>`

/**
 * One slip as semantic HTML. Rules are bordered blocks (they cannot wrap at
 * any font scale), the items block is the dominant element, and a void slip
 * strikes its item names through. Wrap the result in the page skeleton from
 * printKotReceipt.ts — this is the inner markup only.
 */
export function renderKotSlipHtml(s: KotSlip, extraClass = ''): string {
  const parts: string[] = []
  const thick = '<div class="kot-rule kot-rule--thick"></div>'
  const thin = '<div class="kot-rule"></div>'

  parts.push(thick)
  parts.push(el('kot-title', escapeHtml(s.title)))
  for (const b of s.banners) parts.push(el('kot-banner', escapeHtml(b)))
  const tn = (s.table ?? '').trim()
  if (tn) {
    parts.push(thick)
    parts.push(el('kot-table', `TABLE ${escapeHtml(tn.toUpperCase())}`))
  }
  parts.push(thick)

  for (const m of s.meta) {
    const left = escapeHtml((m.left ?? '').trim())
    const right = escapeHtml((m.right ?? '').trim())
    if (!right) {
      if (left) parts.push(el('kot-meta kot-meta--wide', left))
      continue
    }
    parts.push(el('kot-meta', `<span>${left}</span><span>${right}</span>`))
  }

  if (s.note.length > 0) {
    parts.push(thin)
    parts.push(
      el(
        'kot-note',
        el('kot-note-title', '** ORDER NOTE **') + s.note.map((n) => el('kot-note-line', escapeHtml(n))).join(''),
      ),
    )
  }

  parts.push(thin)
  const itemRows: string[] = []
  itemRows.push(el('kot-item kot-item--head', '<span>ITEM</span><span>QTY</span>'))
  for (const it of s.items) {
    const name = escapeHtml((it.name ?? '').trim())
    itemRows.push(
      el('kot-item', `<span class="kot-item-name">${name}</span><span class="kot-item-qty">${it.qty}</span>`),
    )
    for (const raw of it.mods ?? []) {
      const m = raw.trim()
      if (m) itemRows.push(el('kot-item-mod', `- ${escapeHtml(m)}`))
    }
    const note = (it.note ?? '').trim()
    if (note) itemRows.push(el('kot-item-note', `&gt;&gt; ${escapeHtml(note)}`))
  }
  parts.push(el('kot-items', itemRows.join('')))
  parts.push(thick)

  if (s.footer.length > 0) {
    parts.push(el('kot-footer', s.footer.map((f) => el('kot-footer-line', escapeHtml(f))).join('')))
    parts.push(thick)
  }

  const cls = ['kot-slip', `kot-slip--${s.kind}`, extraClass].filter(Boolean).join(' ')
  return `<section class="${cls}">${parts.join('')}</section>`
}

/**
 * Slip styles. Everything is in `em` so the tenant's kot_font_scale (applied
 * as the body font-size by printKotReceipt.ts) scales the whole ticket
 * uniformly. Rules are borders, never characters.
 */
export function kotSlipCss(): string {
  return `
  .kot-rule { border-top: 1px dashed #000; margin: 0.15em 0; height: 0; }
  .kot-rule--thick { border-top: 2px solid #000; }
  .kot-title { text-align: center; font-size: 1.15em; font-weight: 700; letter-spacing: 0.02em; }
  .kot-slip--void .kot-title { background: #000; color: #fff; padding: 0.15em 0; }
  .kot-banner { text-align: center; font-weight: 700; }
  .kot-table { text-align: center; font-size: 1.6em; font-weight: 700; padding: 0.1em 0; }
  .kot-meta { display: flex; justify-content: space-between; gap: 1em; font-size: 0.95em; font-weight: 400; }
  .kot-meta span:last-child { white-space: nowrap; }
  .kot-meta--wide { display: block; overflow-wrap: anywhere; }
  .kot-note { font-size: 0.95em; font-weight: 400; }
  .kot-note-title { font-weight: 700; }
  .kot-note-line { overflow-wrap: anywhere; }
  .kot-items { font-size: 1.35em; line-height: 1.3; font-weight: 700; }
  .kot-item { display: grid; grid-template-columns: minmax(0, 1fr) 36%; column-gap: 0.4em; align-items: start; }
  .kot-item--head { font-size: 0.65em; font-weight: 600; letter-spacing: 0.05em; border-bottom: 1px dashed #000; margin-bottom: 0.2em; }
  .kot-item-name { overflow-wrap: anywhere; }
  .kot-item-qty { text-align: center; }
  /* Heavier than a note: a modifier is an instruction the cook must follow,
     a note is an exception they must notice. */
  .kot-item-mod { font-size: 0.85em; font-weight: 600; padding-left: 1em; overflow-wrap: anywhere; }
  .kot-item-note { font-size: 0.8em; font-weight: 400; padding-left: 1em; overflow-wrap: anywhere; }
  .kot-slip--void .kot-item-name { text-decoration: line-through; text-decoration-thickness: 0.12em; }
  .kot-footer { text-align: center; font-size: 1.1em; font-weight: 700; }
  .kot-slip--void .kot-footer { font-size: 1.25em; }
  `
}
